import { useEffect, useState } from 'react';
import api, { AccessRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg, splitList } from '../utils';

export default function AccessRequestsPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('iga-admin');
  const [rows, setRows] = useState<AccessRequest[]>([]);
  const [error, setError] = useState('');

  // New request form
  const [subjId, setSubjId] = useState('');
  const [subjName, setSubjName] = useState('');
  const [roles, setRoles] = useState('');
  const [justification, setJustification] = useState('');

  const load = async () => {
    setError('');
    try {
      const r = await api.get<AccessRequest[]>('/access-requests');
      setRows(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const create = async () => {
    setError('');
    try {
      await api.post('/access-requests', {
        subjectUserId: subjId,
        subjectUsername: subjName,
        requested: { roles: splitList(roles) },
        justification,
      });
      setSubjId('');
      setSubjName('');
      setRoles('');
      setJustification('');
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const decide = async (id: string, action: 'approve' | 'reject') => {
    setError('');
    try {
      if (action === 'reject') {
        const reason = window.prompt('Ablehnungsgrund?') ?? '';
        await api.post(`/access-requests/${id}/reject`, { reason });
      } else {
        await api.post(`/access-requests/${id}/approve`, {});
      }
      await load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const statusColor: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-300',
    approved: 'bg-green-500/20 text-green-300',
    rejected: 'bg-coral/20 text-coral',
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Access-Requests</h2>
      {error && <p className="text-coral">{error}</p>}

      <section className="card space-y-3">
        <h3 className="font-medium text-coral">Neuer Antrag</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Subject User-ID</label>
            <input className="input" value={subjId} onChange={(e) => setSubjId(e.target.value)} />
          </div>
          <div>
            <label className="label">Subject Username</label>
            <input className="input" value={subjName} onChange={(e) => setSubjName(e.target.value)} />
          </div>
          <div>
            <label className="label">Rollen (Komma-getrennt)</label>
            <input className="input" value={roles} onChange={(e) => setRoles(e.target.value)} />
          </div>
          <div>
            <label className="label">Begründung</label>
            <input
              className="input"
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
            />
          </div>
        </div>
        <button className="btn-primary" disabled={!subjId || !subjName || !roles} onClick={create}>
          Antrag stellen
        </button>
      </section>

      <section className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-edge">
              <th className="th">Subject</th>
              <th className="th">Angefragt</th>
              <th className="th">SoD</th>
              <th className="th">Status</th>
              <th className="th">Antragsteller</th>
              <th className="th"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-edge/50">
                <td className="td">{r.subject_username}</td>
                <td className="td">{(r.requested.roles ?? []).join(', ')}</td>
                <td className="td">
                  {r.sod_violations.length > 0 ? (
                    <span className="badge bg-coral/20 text-coral">
                      {r.sod_violations.map((v) => v.policyName).join(', ')}
                    </span>
                  ) : (
                    <span className="text-gray-500">—</span>
                  )}
                </td>
                <td className="td">
                  <span className={`badge ${statusColor[r.status] ?? ''}`}>{r.status}</span>
                </td>
                <td className="td">{r.created_by_email}</td>
                <td className="td">
                  {isAdmin && r.status === 'pending' && (
                    <div className="flex gap-2">
                      <button className="btn-primary" onClick={() => decide(r.id, 'approve')}>
                        Genehmigen
                      </button>
                      <button className="btn-ghost" onClick={() => decide(r.id, 'reject')}>
                        Ablehnen
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td className="td text-gray-500" colSpan={6}>
                  Keine Anträge.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
